<template>
  <popup-dialog
    :visible="visible"
    class="popup-duplicatemodel"
    :close-button-visible="true"
    :title="$t('labels.newModel')"
    @close="close"
  >
    <form class="popup-duplicatemodel__form" @submit.prevent="copy()">
      <input-workspace-folder
        v-model:workspace-id="workspaceId"
        v-model:folder-id="folderId"
        :errors="{
          workspace: fieldErrors?.workspace_id ?? undefined,
          folder: fieldErrors?.folder_id ?? undefined
        }"
        @update:folder-slug="folderSlug = $event"
        @update:folder-id="fieldErrors.folder_id = null"
        @update:workspace-id="fieldErrors.workspace_id = null"
      />

      <input-text
        v-model:value="newName"
        :autofocus="true"
        :label="$t('labels.name')"
      />

      <button
        class="button popup-duplicatemodel__button mt-4"
        type="submit"
        :disabled="copying"
      >
        <template v-if="copying">
          {{ $t("messages.saving") }}
        </template>
        <template v-else>
          {{ $t("labels.duplicate") }}
        </template>
      </button>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputWorkspaceFolder from "@core/input/InputWorkspaceFolder.vue";
import InputText from "@core/input/InputText.vue";
import { useModels } from "@/api/models.store";
import { usePublicModels } from "@/api/publicModels.store";
import { useSharedModels } from "@/api/sharedModels.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { showError, showMessage } from "@core/ui/alert/alert";
import { getRouteToModel } from "@core/routes/routeDetector";
import type { Model } from "@/api/Model";
import type { PublicModel } from "@/api/PublicModel";
import type { SharedModel } from "@/api/SharedModel";
import type { BadRequestError } from "@core/errors/Errors";

const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
  visible: boolean;
  model?: Model | PublicModel | SharedModel;
}>();

const emit = defineEmits(["close", "duplicated"]);

const { copyModel: copyWorkspaceModel } = useModels();
const { copyPublicModel } = usePublicModels();
const { copySharedModel } = useSharedModels();
const { userWorkspace } = useWorkspaces({});

type CopyErrorFields = {
  workspace_id?: string | null;
  folder_id?: string | null;
}

const workspaceId = ref<number>();
const folderId = ref<number>();
const folderSlug = ref<string>();
const newName = ref("");
const fieldErrors = ref({} as CopyErrorFields);

const copying = ref(false);

watch(() => props.model?.id, () => {
  console.log("model changed", props.model);
  if (props.model == null) {
    return;
  }
  const modelWorkspace = "workspace" in props.model ? props.model.workspace : null;
  const modelFolder = "folder" in props.model ? props.model.folder : null;
  workspaceId.value = modelWorkspace ?? userWorkspace.value?.id ?? undefined;
  folderId.value = modelFolder ?? undefined;
  newName.value = `${props.model?.name}${t("labels.copySuffix")}`;
}, {
  immediate: true
});

type CopyError = BadRequestError & {
  fields: CopyErrorFields;
};
  
async function copy() {
  if (workspaceId.value == null) {
    showMessage(t("messages.chooseWorkspace"));
    return;
  }
  if (folderId.value == null) {
    showMessage(t("messages.chooseFolder"));
    return;
  }

  copying.value = true;
  try {
    const newModel = await copyModel();

    console.log("copied model", newModel);

    router.push(getRouteToModel(newModel));
    close();
    emit("duplicated", newModel);
  } catch (e) {
    const error = e as CopyError;
    fieldErrors.value = error.fields || {};
    showError(error);
  } finally {
    copying.value = false;
  }
}
function copyModel() {
  if (props.model == null) {
    throw new Error("Model must be set");
  }
  if (workspaceId.value == null || folderId.value == null) {
    throw new Error("Workspace and folder must be set");
  }

  const data = {
    workspaceId: workspaceId.value,
    folderId: folderId.value,
    name: newName.value
  };

  if (isPublicModel(props.model)) {
    return copyPublicModel(props.model, data);
  } else if (isSharedModel(props.model)) {
    return copySharedModel(props.model, data);
  } else {
    return copyWorkspaceModel(props.model, data, {});
  }
}
function close() {
  emit("close");
}

function isPublicModel(model: Model | PublicModel | SharedModel): model is PublicModel {
  return model.type === "public_model";
}
function isSharedModel(model: Model | PublicModel | SharedModel): model is SharedModel {
  return model.type === "shared_model";
}
</script>

<style lang="scss">
.popup-duplicatemodel {
  overflow: visible;

  &__form {
    padding: 2rem;
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
  }

  &__button {
    align-self: center;
  }
}
</style>
